import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  isStoredAuthExpired,
  useStoredAuth,
  type StoredAuth,
} from "../../../utils/auth";
import { resolveApiDomain } from "../../../utils/app-domains";

/**
 * Shape of `GET {baseUrl}/notifications` rows, live-verified against
 * `https://api.dev.nazare.app/notifications?limit=20` (nazare-frontend's
 * real notifications endpoint — see `app/web/app/hooks/use-notifications.ts`
 * and `app/web/app/features/sse/sse-topics.ts`'s `notificationsTopic()` for
 * where this same base URL/path is used there).
 *
 * Note `created_at`/`read_at`/`dismissed_at` are epoch-millisecond numbers
 * on the wire, NOT ISO strings — nazare-frontend's own `Notification` type
 * declares them as `string`, but the live payload disagrees (confirmed by
 * fetching the endpoint directly). `new Date(x)` accepts either shape, so
 * this is only a type-accuracy fix here, not a behavior change.
 */
export type Notification = {
  id: string;
  notification_type: string;
  reference_id: string | null;
  title: string;
  body: string | null;
  url: string | null;
  status: string;
  read_at: number | null;
  dismissed_at: number | null;
  created_at: number;
};

// This app has no `getEnv()`/`getServiceDomain()` wrapper (that lives in
// nazare-frontend's app/web only) and packages/ui's tsconfig sets `types: []`
// so `vite/client`'s `ImportMetaEnv` augmentation isn't ambiently available
// here — read `import.meta.env` through a cast rather than adding that
// ambient dependency to the whole package for one component. At runtime
// (bundled by whichever app consumes this component, e.g. `platform`) this
// is the exact same object Vite's `import.meta.env` always is.
function getApiBaseUrl(): string {
  const env = (import.meta as unknown as { env?: Record<string, string | undefined> })
    .env;
  return resolveApiDomain(window.location.origin, env?.VITE_NAZARE_API_DOMAIN);
}

function authHeaders(token: string): HeadersInit {
  return {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };
}

/**
 * Thrown when notifications can't be loaded because the session itself is
 * gone (no refresh token, or refreshing it failed) — as opposed to a
 * generic network/server error. Lets the UI show an honest "sign in again"
 * state instead of a bare "couldn't load" error.
 */
export class SessionExpiredError extends Error {
  constructor() {
    super("Session expired");
    this.name = "SessionExpiredError";
  }
}

type RefreshedAuth = {
  accessToken: string;
  refreshToken: string;
  expiresAt: number;
};

// Module-scoped, session-only cache of a silently-refreshed access token.
// Not persisted to localStorage because this shared UI helper is read-only.
// So a refreshed token here lives only for
// this tab's lifetime; a reload re-reads the (still-stale) stored token,
// sees it's expired again, and silently refreshes again. Wasteful across
// reloads, but correct, and no worse than today's behavior of requiring a
// fresh sign-in.
let refreshedAuth: RefreshedAuth | null = null;

// De-dupes concurrent refresh attempts (e.g. the feed query and a mark-read
// mutation both hitting an expired token at once) into a single in-flight
// network request rather than each firing their own.
let refreshInFlight: Promise<RefreshedAuth | null> | null = null;

// Mirrors nazare-frontend's real `RefreshToken` GraphQL mutation (see
// `app/web/app/screens/login/login.gql.ts`'s generated `RefreshTokenDocument`
// and `app/web/app/screens/login/refreshToken.gql.ts`'s `refreshTokenCall()`)
// as a plain string rather than pulling in the `graphql` package just to
// print an AST this package doesn't otherwise need.
const REFRESH_TOKEN_MUTATION = `
  mutation RefreshToken($token: String!) {
    refreshToken(refreshRequestInput: { refreshToken: $token }) {
      accessToken
      refreshToken
      expiresAt
      user
    }
  }
`;

async function performRefresh(
  baseUrl: string,
  refreshToken: string,
): Promise<RefreshedAuth | null> {
  try {
    const res = await fetch(`${baseUrl}/graphql`, {
      method: "POST",
      headers: authHeaders(refreshToken),
      body: JSON.stringify({
        query: REFRESH_TOKEN_MUTATION,
        variables: { token: refreshToken },
      }),
    });
    if (!res.ok) return null;
    const json: {
      data?: { refreshToken?: Partial<RefreshedAuth> | null };
    } = await res.json();
    const result = json.data?.refreshToken;
    if (
      !result?.accessToken ||
      !result.refreshToken ||
      typeof result.expiresAt !== "number"
    ) {
      return null;
    }
    refreshedAuth = result as RefreshedAuth;
    return refreshedAuth;
  } catch {
    return null;
  }
}

/**
 * Replicates nazare-frontend's real `refreshTokenCall()` — a plain POST to
 * the same GraphQL endpoint using only `StoredAuth.refreshToken`, with no
 * dependency on Apollo or any in-memory OAuth client state, so it's fully
 * reproducible here from just what's already in `localStorage`. De-duped:
 * concurrent callers share one in-flight request.
 */
function refreshAccessToken(
  baseUrl: string,
  refreshToken: string,
): Promise<RefreshedAuth | null> {
  if (!refreshInFlight) {
    refreshInFlight = performRefresh(baseUrl, refreshToken).finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

/** Best refresh token available: a just-refreshed one, else the stored one. */
function currentRefreshToken(auth: StoredAuth | null): string | undefined {
  return refreshedAuth?.refreshToken ?? auth?.refreshToken;
}

/** Attempts one silent refresh; returns the new access token, or null. */
async function forceRefresh(
  auth: StoredAuth | null,
  baseUrl: string,
): Promise<string | null> {
  const refreshToken = currentRefreshToken(auth);
  if (!refreshToken) return null;
  const refreshed = await refreshAccessToken(baseUrl, refreshToken);
  return refreshed?.accessToken ?? null;
}

/**
 * Resolves the access token to send with a request: a still-fresh
 * session-refreshed token if we have one, else the stored token if it's not
 * (yet) locally expired, else one silent refresh attempt. Throws
 * `SessionExpiredError` if none of those produce a usable token.
 */
async function resolveAccessToken(
  auth: StoredAuth | null,
  baseUrl: string,
): Promise<string> {
  if (refreshedAuth && !isStoredAuthExpired(refreshedAuth)) {
    return refreshedAuth.accessToken;
  }
  if (auth?.accessToken && !isStoredAuthExpired(auth)) {
    return auth.accessToken;
  }
  const token = await forceRefresh(auth, baseUrl);
  if (token) return token;
  throw new SessionExpiredError();
}

/**
 * Auth-aware fetch shared by the feed query and both mutations below.
 *
 * Resolves a token via {@link resolveAccessToken} (which already refreshes
 * proactively if the stored token looks locally expired), then makes the
 * request. If the server *still* rejects it with a 401 — the real failure
 * mode confirmed live in dev: `StoredAuth.expiresAt` can be stale/out of
 * sync with the access JWT's own `exp` claim, so a token can look
 * locally-valid and still be genuinely expired server-side — attempts
 * exactly one more silent refresh-and-retry before giving up.
 */
async function authedFetch(
  baseUrl: string,
  auth: StoredAuth | null,
  path: string,
  init: RequestInit = {},
): Promise<Response> {
  const token = await resolveAccessToken(auth, baseUrl);
  const res = await fetch(`${baseUrl}${path}`, {
    ...init,
    headers: authHeaders(token),
  });
  if (res.status !== 401) return res;

  const refreshedToken = await forceRefresh(auth, baseUrl);
  if (!refreshedToken) throw new SessionExpiredError();

  return fetch(`${baseUrl}${path}`, {
    ...init,
    headers: authHeaders(refreshedToken),
  });
}

async function fetchNotifications(
  baseUrl: string,
  auth: StoredAuth | null,
): Promise<Notification[]> {
  const res = await authedFetch(baseUrl, auth, "/notifications?limit=20");
  if (!res.ok) {
    if (res.status === 401) throw new SessionExpiredError();
    throw new Error(`Failed to fetch notifications: ${res.status}`);
  }
  const data: { notifications: Notification[] } = await res.json();
  return data.notifications;
}

const NOTIFICATIONS_QUERY_KEY = ["app-shell", "notifications"] as const;

/**
 * Live notification feed for `<NotificationBell>`. Polls on an interval
 * rather than subscribing to nazare-frontend's SSE stream
 * (`{baseUrl}/notifications/stream`, run through a cross-tab `SharedWorker`
 * in `app/web/app/features/sse/`) — reproducing that worker/reconnect/replay
 * machinery is out of scope for a single app-shell component, and polling a
 * `useQuery` is what this repo's own `createQueryClient()` defaults (see
 * `utils/shell-state/query-client.ts`) are tuned for.
 *
 * Disabled (no fetch attempted) when there's no stored auth session at all.
 * A
 * present-but-expired access token is *not* treated as "no session": as
 * long as a refresh token exists, `fetchNotifications` will try one silent
 * refresh before failing (see `authedFetch`/`resolveAccessToken`).
 */
export function useNotificationFeed() {
  const auth = useStoredAuth();
  const baseUrl = getApiBaseUrl();
  const hasSession = Boolean(auth?.accessToken || auth?.refreshToken);

  const {
    data: notifications = [],
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: NOTIFICATIONS_QUERY_KEY,
    queryFn: () => fetchNotifications(baseUrl, auth),
    enabled: hasSession,
    refetchInterval: 60_000,
  });

  const unreadCount = notifications.reduce(
    (count, n) => (n.read_at == null ? count + 1 : count),
    0,
  );

  return {
    notifications,
    unreadCount,
    hasUnread: unreadCount > 0,
    isLoading,
    isError,
    // Distinguishes "your session is gone, sign in again" from a generic
    // fetch/server error — see `SessionExpiredError`.
    isSessionExpired: error instanceof SessionExpiredError,
  };
}

/** Marks a single notification read; optimistic, rolls back on error. */
export function useMarkNotificationRead() {
  const queryClient = useQueryClient();
  const auth = useStoredAuth();
  const baseUrl = getApiBaseUrl();

  return useMutation({
    mutationFn: async (id: string) => {
      if (!auth?.accessToken && !auth?.refreshToken) return;
      const res = await authedFetch(baseUrl, auth, `/notifications/${id}/read`, {
        method: "PATCH",
      });
      if (!res.ok) {
        throw new Error(`Failed to mark notification read: ${res.status}`);
      }
    },
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: NOTIFICATIONS_QUERY_KEY });
      const previous = queryClient.getQueryData<Notification[]>(NOTIFICATIONS_QUERY_KEY);
      queryClient.setQueryData<Notification[]>(NOTIFICATIONS_QUERY_KEY, (old) =>
        (old ?? []).map((n) => (n.id === id ? { ...n, read_at: n.read_at ?? Date.now() } : n)),
      );
      return { previous };
    },
    onError: (error, _id, context) => {
      console.error("[notification-bell]", error);
      if (context?.previous) {
        queryClient.setQueryData(NOTIFICATIONS_QUERY_KEY, context.previous);
      }
    },
  });
}

/** Marks every notification read; optimistic, rolls back on error. */
export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();
  const auth = useStoredAuth();
  const baseUrl = getApiBaseUrl();

  return useMutation({
    mutationFn: async () => {
      if (!auth?.accessToken && !auth?.refreshToken) return;
      const res = await authedFetch(baseUrl, auth, "/notifications/read-all", {
        method: "POST",
      });
      if (!res.ok) {
        throw new Error(`Failed to mark all notifications read: ${res.status}`);
      }
    },
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: NOTIFICATIONS_QUERY_KEY });
      const previous = queryClient.getQueryData<Notification[]>(NOTIFICATIONS_QUERY_KEY);
      queryClient.setQueryData<Notification[]>(NOTIFICATIONS_QUERY_KEY, (old) =>
        (old ?? []).map((n) => ({ ...n, read_at: n.read_at ?? Date.now() })),
      );
      return { previous };
    },
    onError: (error, _vars, context) => {
      console.error("[notification-bell]", error);
      if (context?.previous) {
        queryClient.setQueryData(NOTIFICATIONS_QUERY_KEY, context.previous);
      }
    },
  });
}
